import { ChevronRight, Home } from 'lucide-react';
import { type ComponentPropsWithoutRef, Fragment, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface BreadcrumbItem {
  label: string;
  /** Anchor href. Omit for the current (last) segment. */
  href?: string;
  /** Optional leading icon. */
  icon?: ReactNode;
}

export interface BreadcrumbsProps extends ComponentPropsWithoutRef<'nav'> {
  items: BreadcrumbItem[];
  /** Show a Home icon as the very first segment, linking to `homeHref`. */
  homeHref?: string;
  /** Maximum visible segments before middle ellipsis kicks in. Default 4. */
  maxVisible?: number;
  /** Render prop for the link element. Defaults to native `<a>`. Useful for router integration. */
  renderLink?: (item: { href: string; children: ReactNode; className?: string }) => ReactNode;
}

/**
 * Wayfinding breadcrumbs. Last segment is the current location (no link).
 * For long trails, the middle is collapsed to "…" and only first + last few stay visible.
 *
 * Pass `renderLink` to integrate with TanStack Router's `<Link>` (or any router):
 *   <Breadcrumbs items={...} renderLink={({href, children, className}) => (
 *     <Link to={href} className={className}>{children}</Link>
 *   )} />
 */
export const Breadcrumbs = ({
  items,
  homeHref,
  maxVisible = 4,
  renderLink,
  className,
  ...rest
}: BreadcrumbsProps) => {
  const fullItems: BreadcrumbItem[] = homeHref
    ? [{ label: 'Início', href: homeHref, icon: <Home className="h-3.5 w-3.5" /> }, ...items]
    : items;

  const renderedItems =
    fullItems.length > maxVisible
      ? [
          fullItems[0],
          { label: '…', href: undefined } as BreadcrumbItem,
          ...fullItems.slice(fullItems.length - (maxVisible - 2)),
        ]
      : fullItems;

  const link = (item: BreadcrumbItem, isLast: boolean) => {
    const content = (
      <span className="inline-flex items-center gap-1.5">
        {item.icon}
        {item.label}
      </span>
    );

    if (isLast || !item.href) {
      return (
        <span
          aria-current={isLast ? 'page' : undefined}
          className={cn(
            'inline-flex items-center gap-1.5 truncate',
            isLast ? 'font-medium text-fg' : 'text-muted-fg',
          )}
        >
          {content}
        </span>
      );
    }

    if (renderLink) {
      return renderLink({
        href: item.href,
        className:
          'inline-flex items-center gap-1.5 truncate text-muted-fg transition-colors hover:text-fg',
        children: content,
      });
    }

    return (
      <a
        href={item.href}
        className="inline-flex items-center gap-1.5 truncate text-muted-fg transition-colors hover:text-fg"
      >
        {content}
      </a>
    );
  };

  return (
    <nav aria-label="Wayfinding" className={cn('text-sm', className)} {...rest}>
      <ol className="flex flex-wrap items-center gap-1.5">
        {renderedItems.map((item, index) => {
          const isLast = index === renderedItems.length - 1;
          if (!item) return null;
          const key = `${item.href ?? 'current'}::${item.label}`;
          return (
            <Fragment key={key}>
              <li className="flex min-w-0 items-center">{link(item, isLast)}</li>
              {!isLast && (
                <li aria-hidden="true" className="text-muted-fg">
                  <ChevronRight className="h-3.5 w-3.5" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};
